import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import "./parent.css";

function fileUrl(path) {
  if (!path) return "";
  return path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
}

function materialIcon(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("video")) return "▶";
  if (t.includes("lecture")) return "📘";
  if (t.includes("ppt")) return "📊";
  if (t.includes("sheet")) return "📋";
  return "📎";
}

function ParentSubjectDetail() {
  const { studentId, courseId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/parents/me/children/${studentId}/subjects/${courseId}`,
          { headers: getAuthHeaders() },
        );
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!res.ok) throw new Error("Failed to load subject");
        if (alive) setDetail(await res.json());
      } catch (err) {
        console.error(err);
        if (alive) setDetail(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentId, courseId]);

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading subject details…</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="pp-empty">
        <p>Subject details unavailable.</p>
        <button
          type="button"
          className="pp-btn pp-btn--ghost pp-btn--icon"
          aria-label="Back"
          onClick={() => navigate(`/parent/children/${studentId}`)}
        >
          ←
        </button>
      </div>
    );
  }

  const att = detail.attendance || {};

  return (
    <div className="pp-page pp-animate-in">
      <div className="pp-detail-top">
        <button
          type="button"
          className="pp-btn pp-btn--ghost pp-btn--icon"
          aria-label="Back"
          onClick={() => navigate(`/parent/children/${studentId}`)}
        >
          ←
        </button>
        <div>
          <p className="pp-kicker pp-kicker--dark">Subject</p>
          <h1>{detail.course_name}</h1>
          <p>
            {detail.teacher_name || "Teacher TBD"}
            {detail.section ? ` · Section ${detail.section}` : ""}
          </p>
        </div>
      </div>

      <section className="pp-stats">
        <article className="pp-stat-card pp-stat-card--teal">
          <h3>{att.attendance_rate || 0}%</h3>
          <p>Subject attendance rate</p>
        </article>
        <article className="pp-stat-card pp-stat-card--green">
          <h3>{att.present || 0}</h3>
          <p>Present</p>
        </article>
        <article className="pp-stat-card pp-stat-card--rose">
          <h3>{att.absent || 0}</h3>
          <p>Absent</p>
        </article>
        <article className="pp-stat-card pp-stat-card--gold">
          <h3>{(detail.materials || []).length}</h3>
          <p>Materials</p>
        </article>
      </section>

      <section className="pp-subject-flow">
        <article className="pp-panel pp-flow-panel">
          <div className="pp-flow-head">
            <span className="pp-flow-step">1</span>
            <h2>Class &amp; files</h2>
          </div>
          {(detail.materials || []).length === 0 ? (
            <p className="pp-muted">No class files uploaded for this subject yet.</p>
          ) : (
            <ul className="pp-material-list">
              {(detail.materials || []).map((m) => (
                <li key={m.id} className="pp-material-item">
                  <span className="pp-material-icon" aria-hidden="true">
                    {materialIcon(m.material_type)}
                  </span>
                  <div className="pp-material-body">
                    <strong>{m.title}</strong>
                    <span className="pp-material-meta">
                      {m.material_type}
                      {m.teacher_name ? ` · ${m.teacher_name}` : ""}
                    </span>
                    {m.description ? <p className="pp-material-desc">{m.description}</p> : null}
                  </div>
                  <a
                    className="pp-btn pp-btn--primary pp-btn--sm"
                    href={fileUrl(m.file_path)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open
                  </a>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article
          className="pp-panel pp-flow-panel pp-flow-panel--link"
          role="link"
          tabIndex={0}
          onClick={() => navigate(`/parent/children/${studentId}/subjects/${courseId}/attendance`)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(`/parent/children/${studentId}/subjects/${courseId}/attendance`);
            }
          }}
        >
          <div className="pp-flow-head">
            <span className="pp-flow-step">2</span>
            <h2>Attendance</h2>
          </div>
          <p className="pp-flow-note">Subject-wise, period-wise attendance records for your child.</p>
          <div className="pp-att-card-summary">
            <span>
              <strong>{att.present || 0}</strong> Present
            </span>
            <span>
              <strong>{att.absent || 0}</strong> Absent
            </span>
            <span>
              <strong>{att.total || 0}</strong> Marked
            </span>
          </div>
          <span className="pp-link">Open attendance table →</span>
        </article>

        <article className="pp-panel pp-flow-panel">
          <div className="pp-flow-head">
            <span className="pp-flow-step">3</span>
            <h2>Teacher remarks</h2>
          </div>
          <ul className="pp-list">
            {(detail.remarks || []).length === 0 && (
              <li className="pp-muted">No remarks for this subject yet.</li>
            )}
            {(detail.remarks || []).map((r) => (
              <li key={r.id} className="pp-list--stack">
                <strong>{r.remark_date}</strong>
                <span>{r.remark}</span>
                {r.teacher_name ? <small className="pp-muted">— {r.teacher_name}</small> : null}
              </li>
            ))}
          </ul>
        </article>

        <article className="pp-panel pp-flow-panel">
          <div className="pp-flow-head">
            <span className="pp-flow-step">4</span>
            <h2>Homework</h2>
          </div>
          <ul className="pp-list">
            {(detail.homework || []).length === 0 && (
              <li className="pp-muted">No homework assigned for this subject.</li>
            )}
            {(detail.homework || []).map((h) => (
              <li key={h.id} className="pp-list--stack pp-homework-item">
                <strong>{h.title}</strong>
                {h.description ? <span>{h.description}</span> : null}
                <small className="pp-muted">Due: {h.due_date || "—"}</small>
                {h.attachment_path ? (
                  <a
                    className="pp-link-inline"
                    href={fileUrl(h.attachment_path)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download {h.attachment_name || "attachment"}
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </article>
      </section>
    </div>
  );
}

export default ParentSubjectDetail;
